<template>
  <ValidationObserver v-slot="{ handleSubmit, invalid ,reset}" ref="observer">
    <artisio-modal size="md" :visible="modal.show" :data="model"
                   :title="model.uuid ? $t(`Update Commission Bid`) : $t(`Create Commission Bid`)" @show="onShow"
                   @hidden="onHidden" @ok.prevent="handleSubmit(onSubmit)" :show-confirmation="showConfirmation"
                   :ok-title="$t('Submit')" :is-loading="loading" scrollable>
      <div class="row mb-4">
        <div class="col-md-12">
          <div v-if="model.bidder">
            <h5 class="mb-0">
              {{ model.bidder.customer.display_name }}
            </h5>
            <p class="mb-0" v-if="model.bidder.customer && model.bidder.customer.billing_address"
               v-html="$options.filters.asAddress(model.bidder.customer.billing_address, false)">
            </p>
            <template v-if="model.bidder.customer.email">
              <p class="m-0">
                {{ model.bidder.customer.email }}
              </p>
              <p class="m-0" v-if="model.bidder.language">
                {{ $t("Language") }}: {{ model.bidder.language.name }}
              </p>
            </template>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-md-6">
          <input-widget :model="model" attribute="paddle_number" readonly/>
        </div>
        <div class="col-md-6">
          <input-widget :model="model" attribute="lot_no" readonly/>
        </div>
        <div class="col-md-6">
          <input-widget :model="model" attribute="type" type="select2" value-field="value" display-field="${item.key}"
                        :items="availableTypes"/>
        </div>
        <div class="col-md-6">
          <currency-input-widget :rules="model.type === 'commission' ? 'required' : ''" :model="model"
                                 :currency="model.bidder ? model.bidder.auction.currency.symbol : null"
                                 attribute="amount"/>
        </div>
        <div class="col-md-6">
          <input-widget :model="model" attribute="status" value-field="value" display-field="${item.key}"
                        type="select2" :items="statuses"/>
        </div>
        <div class="col-md-6" v-if="model.type !== 'max_bid'">
          <clerk-select-input-widget :model="model" attribute="clerk_uuid" :auction="modal.sale"/>
        </div>
        <div class="col-md-6">
          <input-widget :model="model" attribute="extra_step" rules="min_value:1|max_value:100"/>
        </div>
        <div class="col-md-6" v-if="model.type !== 'max_bid'">
          <input-widget :model="model" attribute="phone" :rules="phoneRules"/>
        </div>
        <div class="col-md-6" v-if="model.type !== 'max_bid'">
          <input-widget :model="model" attribute="phone2"/>
        </div>
        <div class="col-md-6">
          <input-widget :model="model" attribute="date" type="datepicker" :rules="model.time ? 'required' : ''"/>
        </div>
        <div class="col-md-6">
          <input-widget :model="model" attribute="time" type="timepicker" :rules="model.date ? 'required' : ''"/>
        </div>
        <div class="col-sm-12">
          <input-widget :model="model" type="textarea" attribute="description"/>
        </div>
      </div>
    </artisio-modal>
  </ValidationObserver>
</template>

<script>
  import InputWidget from "../../../core/components/input-widget/InputWidget.vue";
  import {createNamespacedHelpers} from "vuex";
  import ArtisioModal from "../../../core/components/artisio-modal/ArtisioModal.vue";
  import BidModel from "../models/Bid.model";
  import i18n from "../../../shared/i18n";
  import CurrencyInputWidget from "../../../core/widgets/CurrencyInputWidget.vue";
  import {getPhoneRuleObj} from "@/plugins/vee-validate";
  import {Formatter} from "@/core/services/formatter.service";
  import ClerkSelectInputWidget from "@/core/widgets/ClerkSelectInputWidget";

  const {mapState, mapActions} = createNamespacedHelpers('preBids');

  export default {
    name: "PreBidEditModal",
    components: {
      ClerkSelectInputWidget,
      InputWidget, ArtisioModal, CurrencyInputWidget
    },
    data() {
      return {
        loading: false,
        showConfirmation: true,
        model: new BidModel(),
        statuses: [
          {key: i18n.t('Rejected'), value: 'rejected'},
          {key: i18n.t('Pending'), value: 'pending'},
          {key: i18n.t('Confirmed'), value: 'confirmed'},
        ],
      }
    },
    computed: {
      ...mapState({
        modal: state => state.preBidEditModal,
        editObject: state => state.preBidEditModal.object
      }),
      availableTypes() {
        if (this.modal && this.modal.sale && this.modal.sale.type === 'timed') {
          return [
            {value: 'max_bid', key: i18n.t('Max Bid')},
          ]
        }
        return [
          {value: 'commission', key: i18n.t('Commission')},
          {value: 'telephone', key: i18n.t('Telephone')},
        ]
      },
      phoneRules() {
        return this.model ? getPhoneRuleObj(this.model.type === 'telephone') : ''
      }
    },
    watch: {
      editObject() {
        if (this.editObject && this.editObject.uuid) {
          this.model = new BidModel(this.editObject);
        }
      },
    },
    methods: {
      ...mapActions(['hidePreBidEditModal', 'updatePreBid', 'createPreBid']),
      onShow() {
        this.showConfirmation = true
      },
      onHidden() {
        this.model = new BidModel();
        this.hidePreBidEditModal();
      },
      async onSubmit() {
        let response;
        this.loading = true;
        let date = this.model.date;
        if (this.model.type === 'telephone') {
          this.model.amount = this.model.amount ? this.model.amount : 0
        }

        if (this.model.date) {
          date = Formatter.getDateAndTimeToSystemFormat(this.model.date, this.model.time)
        }
        if (this.model.extra_step === '') {
          this.model.extra_step = null
        }
        let data = _.cloneDeep(this.model)
        data.date = date
        if (!this.model.uuid) {
          response = await this.createPreBid(data);
        } else {
          response = await this.updatePreBid(data);
        }
        this.loading = false;

        if (response.success) {
          this.showConfirmation = false;
          this.hidePreBidEditModal();

          if (this.model.uuid) {
            this.$toast('success', this.$t('Commission bid was successfully updated'));
          }
        } else {
          if (response.toastError) {
            this.$errorDialog(response.toastError);
          } else {
            if (response.body.non_field_errors) {
              this.$errorDialog(response.body.non_field_errors[0]);
            } else {
              this.model.setMultipleErrors(response.body);
            }
          }
        }
      },
    }
  }
</script>

<style scoped>

</style>
